import { useState } from 'react'
import Button from '../ui/Button'
import Modal from '../ui/Modal'
import { parseDecklist } from '../../utils/decklist'
import { useListsStore } from '../../stores/listsStore'
import { decksApi } from '../../api/client'
import type { Tcg } from '../../types'

/** Human-readable label per TCG (selector + badges). */
const TCG_LABEL: Record<Tcg, string> = {
  mtg: 'Magic: The Gathering',
  yugioh: 'Yu-Gi-Oh!',
  onepiece: 'One Piece',
  lorcana: 'Disney Lorcana',
  riftbound: 'Riftbound',
}

/** Per-TCG example shown inside the picker modal (what the list should look like). */
const TCG_EXAMPLE: Record<Tcg, string> = {
  mtg: '4 Lightning Bolt\n2 Counterspell\n1 Black Lotus',
  yugioh: '3 Dark Magician\n2 Blue-Eyes White Dragon\n1 89631139',
  onepiece: '4 OP01-001\n4 Monkey D. Luffy\n1 OP01-003',
  lorcana: '4 Ariel - On Human Legs\n2 Elsa - Spirit of Winter',
  riftbound: '1 OGN-001\n4 OGN-100',
}

const GENERIC_PLACEHOLDER =
  '4 Lightning Bolt\n2 Counterspell\n1 Black Lotus\n\nsideboard\n2 Pyroblast\n1 Hydroblast'

const GENERIC_LIST_HELP =
  'Acepta texto plano (4 Lightning Bolt), formato Arena (4 Lightning Bolt (M11) 146) y CSV de Moxfield/TappedOut (conservan el arte: set + nº de coleccionista). Al procesar te pediremos el juego de la lista para resolverla contra la base de datos correcta.'

/**
 * Form to create a new "Lista" (deck). Two ways to add cards, in one view:
 * - URL input (top): paste a deck URL — the backend fetches it and
 *   auto-detects the TCG from the domain (no selector needed). A checkbox
 *   lets the user include the deck's tokens as printable cards (T-05).
 * - Textarea (bottom): paste a decklist (parsed locally, no backend). Because
 *   the text is parsed locally we cannot know its game, so a modal asks the
 *   user to pick the TCG for the list before it is created.
 *
 * On success a new independent list is created and persisted to localStorage.
 */
export default function NewListForm() {
  const addList = useListsStore((s) => s.addList)

  // --- Shared ---
  const [name, setName] = useState('')

  // --- URL input ---
  const [url, setUrl] = useState('')
  const [urlLoading, setUrlLoading] = useState(false)
  const [urlError, setUrlError] = useState<string | null>(null)
  /** T-05: include the deck's tokens as printable cards (default on, like the original). */
  const [includeTokens, setIncludeTokens] = useState(true)

  // --- Text input ---
  const [text, setText] = useState('')
  const [unparsed, setUnparsed] = useState<string[]>([])

  // --- TCG picker modal (text input) ---
  const [tcgModalOpen, setTcgModalOpen] = useState(false)
  const [selectedTcg, setSelectedTcg] = useState<Tcg>('mtg')

  /** Open the TCG picker before creating the list (text is parsed on confirm). */
  const handleCreate = () => {
    const { mainboard, sideboard } = parseDecklist(text)
    if (mainboard.length === 0 && sideboard.length === 0) return
    setSelectedTcg('mtg')
    setTcgModalOpen(true)
  }

  /** Create the list with the TCG chosen in the modal. */
  const confirmCreate = (tcg: Tcg) => {
    const { mainboard, sideboard, unparsed: bad } = parseDecklist(text)
    if (mainboard.length === 0 && sideboard.length === 0) {
      setTcgModalOpen(false)
      return
    }
    // Tag every card with the chosen game so mixed-TCG orders work.
    const tag = (c: (typeof mainboard)[number]) => ({ ...c, game: tcg })
    addList(name.trim() || null, mainboard.map(tag), sideboard.map(tag), tcg)
    setUnparsed(bad)
    setName('')
    setText('')
    setTcgModalOpen(false)
  }

  const handleImport = async () => {
    const trimmed = url.trim()
    if (!trimmed) return
    setUrlLoading(true)
    setUrlError(null)
    try {
      const deck = await decksApi.import(trimmed, includeTokens)
      // Mark failed cards as unresolved so they show in red in the list.
      const failedNames = new Set(deck.failures.map((f) => f.name.toLowerCase()))
      const markUnresolved = (c: (typeof deck.main)[number]) => ({
        ...c,
        unresolved: failedNames.has(c.name.toLowerCase()),
        unresolved_reason: failedNames.has(c.name.toLowerCase())
          ? deck.failures.find((f) => f.name.toLowerCase() === c.name.toLowerCase())?.reason
          : undefined,
      })
      const mainWithFlags = deck.main.map(markUnresolved)
      const sideWithFlags = deck.side.map(markUnresolved)
      // The backend reports the deck's TCG; cards already carry their `game`.
      addList(deck.name || null, mainWithFlags, sideWithFlags, deck.tcg)
      // T-05: surface importer warnings (e.g. "Deckstats no expone los tokens...").
      if (deck.warnings.length > 0) {
        setUrlError(deck.warnings.join(' '))
      } else if (deck.failures.length > 0) {
        setUrlError(
          `Importado con ${deck.failures.length} carta(s) sin resolver: ` +
            deck.failures
              .slice(0, 3)
              .map((f) => f.name)
              .join(', ') +
            (deck.failures.length > 3 ? '…' : '')
        )
      }
      setUrl('')
    } catch (err) {
      setUrlError(
        err instanceof Error ? err.message : 'No se pudo importar el mazo. Revisa la URL.'
      )
    } finally {
      setUrlLoading(false)
    }
  }

  const totalLines = text.trim() ? text.trim().split('\n').filter((l) => l.trim()).length : 0

  return (
    <div className="space-y-4">
      {/* URL import (auto-detects the TCG from the domain). */}
      <div>
        <label htmlFor="deck-url" className="block text-sm font-medium text-gray-700 mb-1">
          Importar desde URL
        </label>
        <div className="flex gap-2">
          <input
            id="deck-url"
            type="url"
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="https://archidekt.com/decks/..."
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void handleImport()
            }}
          />
          <Button onClick={() => void handleImport()} disabled={!url.trim() || urlLoading}>
            {urlLoading ? 'Importando…' : 'Importar'}
          </Button>
        </div>
        {/* T-05: token selector for the 5 MTG importers (Moxfield, Archidekt,
            Deckstats, TappedOut, ManaBox). Non-MTG URLs simply ignore it. */}
        <label
          htmlFor="include-tokens"
          className="mt-2 flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none"
        >
          <input
            id="include-tokens"
            type="checkbox"
            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            checked={includeTokens}
            onChange={(e) => setIncludeTokens(e.target.checked)}
          />
          Incluir tokens y extras
        </label>
        <p className="text-xs text-gray-500 mt-1">
          El juego se detecta automáticamente desde la URL. TCG soportados:{' '}
          <strong>MTG</strong>, <strong>Yu-Gi-Oh!</strong>, <strong>One Piece</strong>,{' '}
          <strong>Lorcana</strong> y <strong>Riftbound</strong>. Consulta la lista de la izquierda
          para ver las webs soportadas.
        </p>
        {urlError && (
          <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded text-amber-700 text-sm">
            {urlError}
          </div>
        )}
      </div>

      <div className="flex items-center gap-3">
        <div className="flex-1 border-t border-gray-200" />
        <span className="text-xs font-medium text-gray-400 uppercase">o pega la lista</span>
        <div className="flex-1 border-t border-gray-200" />
      </div>

      {/* Text input (parsed locally; asks for the TCG via modal). */}
      <div>
        <label htmlFor="list-name" className="block text-sm font-medium text-gray-700 mb-1">
          Nombre de la lista (opcional)
        </label>
        <input
          id="list-name"
          type="text"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Ej. Mazo Goblins (si lo dejas vacío: Lista 1, Lista 2...)"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </div>

      <div>
        <label htmlFor="list-cards" className="block text-sm font-medium text-gray-700 mb-1">
          Decklist (una carta por línea)
        </label>
        <textarea
          id="list-cards"
          className="w-full h-48 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
          placeholder={GENERIC_PLACEHOLDER}
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
        <p className="text-xs text-gray-500 mt-1">
          Detecta <strong>sideboard</strong> o <strong>banquillo</strong> como sección separada.{' '}
          {GENERIC_LIST_HELP}
        </p>
      </div>

      {unparsed.length > 0 && (
        <div className="p-3 bg-amber-50 border border-amber-200 rounded text-amber-700 text-sm">
          <strong>{unparsed.length}</strong> línea(s) no reconocidas:{' '}
          {unparsed.slice(0, 5).join('; ')}
          {unparsed.length > 5 ? '…' : ''}
        </div>
      )}

      <Button onClick={handleCreate} disabled={totalLines === 0}>
        Procesar lista
      </Button>

      {/* TCG picker for text lists — the game can't be inferred from free text,
          so the user picks it here and the list (and its PDF guides) use it. */}
      <Modal
        isOpen={tcgModalOpen}
        onClose={() => setTcgModalOpen(false)}
        title="¿De qué juego es esta lista?"
      >
        <p className="text-sm text-gray-600 mb-4">
          El texto no indica el juego. Elige el TCG para resolver las cartas contra la base de
          datos correcta y aplicar el formato de guías adecuado en el PDF.
        </p>
        <div className="space-y-2">
          {(Object.keys(TCG_LABEL) as Tcg[]).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setSelectedTcg(t)}
              className={`w-full text-left px-3 py-2 rounded-lg border transition-colors ${
                selectedTcg === t
                  ? 'border-blue-600 bg-blue-50 ring-2 ring-blue-500'
                  : 'border-gray-300 hover:bg-gray-50'
              }`}
            >
              <span className="block text-sm font-medium text-gray-900">{TCG_LABEL[t]}</span>
              <span className="block text-xs text-gray-500 font-mono whitespace-pre-line mt-0.5">
                {TCG_EXAMPLE[t]}
              </span>
            </button>
          ))}
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button
            type="button"
            onClick={() => setTcgModalOpen(false)}
            className="px-4 py-2 text-sm font-medium rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200"
          >
            Cancelar
          </button>
          <Button onClick={() => confirmCreate(selectedTcg)}>Crear lista</Button>
        </div>
      </Modal>
    </div>
  )
}
